<template>
  <ModalComp
    :isVisible="isVisible"
    title="Création d'un code promo"
    size="lg"
    @update:isVisible="$emit('update:isVisible', $event)"
  >
    <template #body>
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Code Promo et Acheteur -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-black mb-2">Code Promo <span class="text-red-500">*</span></label>
            <input
              v-model="formData.code"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-black mb-2">Acheteur</label>
            <BuyerAutocomplete
              ref="buyerAutocompleteRef"
              v-model="formData.acheteurId"
              placeholder="Rechercher un acheteur..."
              @update:modelValue="handleBuyerChange"
            />
            <div class="mt-2 flex items-center gap-2">
              <button
                type="button"
                @click="clearBuyer"
                class="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Effacer la sélection
              </button>
              <span class="text-sm text-black">
                (Laisser vide pour cibler tous les acheteurs)
              </span>
            </div>
          </div>
        </div>

        <!-- Type, Montant et Montant minimum -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-black mb-2">Type <span class="text-red-500">*</span></label>
            <select
              v-model="formData.type"
              @change="handleTypeChange"
              :disabled="formData.offert"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              :class="formData.offert ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'"
              required
            >
              <option value="percentage">Pourcentage</option>
              <option value="fixed">Montant fixe</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-black mb-2">Montant de la promo <span class="text-red-500">*</span></label>
            <input
              v-model="formData.montant"
              @input="handleMontantChange"
              @wheel="handleNumberWheel"
              type="number"
              step="0.01"
              :min="formData.type === 'percentage' ? '1' : '0'"
              :max="formData.type === 'percentage' ? '100' : ''"
              :disabled="formData.offert"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              :class="formData.offert ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'"
              required
            />
            <div v-if="formData.offert" class="mt-1 text-xs text-black italic">
              🔒 Verrouillé à 100% car "Offert" est activé
            </div>
            <div v-if="validationErrors.montant" class="text-red-500 text-sm mt-1">
              {{ validationErrors.montant }}
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-black mb-2">Montant minimum</label>
            <input
              v-model="formData.montantMin"
              @wheel="handleNumberWheel"
              type="number"
              step="0.01"
              min="0"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div v-if="validationErrors.montantMin" class="text-red-500 text-sm mt-1">
              {{ validationErrors.montantMin }}
            </div>
          </div>
        </div>


        <!-- Localisation et Actif -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-black mb-2">Localisation <span class="text-red-500">*</span></label>
            <select
              v-model="formData.localisation"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="1">Tous</option>
              <option value="2">France</option>
              <option value="3">DOM-TOM</option>
              <option value="5">Europe</option>
              <option value="4">International</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-black mb-2">Actif <span class="text-red-500">*</span></label>
            <select
              v-model="formData.actif"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="false">Non</option>
              <option value="true">Oui</option>
            </select>
          </div>
        </div>

        <!-- Utilisation et Première commande -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-black mb-2">Utilisation <span class="text-red-500">*</span></label>
            <div class="flex items-center space-x-6">
              <label class="flex items-center">
                <input
                  v-model="formData.utilisation"
                  type="radio"
                  value="unique"
                  :disabled="formData.premiereCommande"
                  class="mr-2 text-blue-600 focus:ring-blue-500"
                  :class="formData.premiereCommande ? 'opacity-50 cursor-not-allowed' : ''"
                />
                <span class="text-sm text-black" :class="formData.premiereCommande ? 'text-black' : ''">Unique</span>
              </label>
              <label class="flex items-center">
                <input
                  v-model="formData.utilisation"
                  type="radio"
                  value="multiple"
                  :disabled="formData.premiereCommande"
                  class="mr-2 text-blue-600 focus:ring-blue-500"
                  :class="formData.premiereCommande ? 'opacity-50 cursor-not-allowed' : ''"
                />
                <span class="text-sm text-black" :class="formData.premiereCommande ? 'text-black' : ''">Multiple</span>
              </label>
            </div>
            <div v-if="formData.premiereCommande" class="mt-1 text-xs text-black italic">
              🔒 Verrouillé car "Réservé aux nouveaux clients" est activé
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-black mb-2">Première commande uniquement</label>
            <label class="flex items-center">
              <input
                v-model="formData.premiereCommande"
                @change="handlePremiereCommandeChange"
                type="checkbox"
                class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <span class="ml-2 text-sm text-black">Réservé aux nouveaux clients</span>
            </label>
          </div>
        </div>

        <!-- Offert et Cumulable -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-black mb-2">Offert</label>
            <label class="flex items-center">
              <input
                v-model="formData.offert"
                type="checkbox"
                class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                @change="handleOffertChange"
              />
              <span class="ml-2 text-sm text-black">Code promo à 100% (gratuit)</span>
            </label>
            <div class="mt-1 text-xs text-black italic">
              Commande gratuite (codes de bienvenue, compensation, etc.)
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-black mb-2">Cumulable</label>
            <label class="flex items-center">
              <input
                v-model="formData.cumulable"
                type="checkbox"
                class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <span class="ml-2 text-sm text-black">Peut être combiné avec d'autres codes</span>
            </label>
          </div>
        </div>

        <!-- Dates de début et fin -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-black mb-2">Début (jj/mm/aaaa) <span class="text-red-500">*</span></label>
            <input
              v-model="formData.dateDebut"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-black mb-2">Fin (jj/mm/aaaa) <span class="text-red-500">*</span></label>
            <input
              v-model="formData.dateFin"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
            <div v-if="validationErrors.dateRange" class="text-red-500 text-sm mt-1">
              {{ validationErrors.dateRange }}
            </div>
          </div>
        </div>
      </form>
    </template>
    
    <template #footer>
      <div class="flex justify-end space-x-3">
        <button
          @click="$emit('update:isVisible', false)"
          type="button"
          class="px-4 py-2 text-black bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          @click="handleSubmit"
          :disabled="!isFormValid || isSubmitting"
          class="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none transition-colors inline-flex items-center gap-2"
          :class="(!isFormValid || isSubmitting) ? 'opacity-50 cursor-not-allowed' : ''"
        >
          <svg v-if="isSubmitting" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
          <span>{{ isSubmitting ? 'Ajout...' : 'Ajouter' }}</span>
        </button>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { reactive, watch, ref, computed } from 'vue'
import { ModalComp } from '@/components'
import BuyerAutocomplete from '@/components/forms/BuyerAutocomplete.vue'
import type { AddPromoModalProps, AddPromoModalEmits, PromoFormData } from '@/types/promoModal'

const props = defineProps<AddPromoModalProps>()
const emit = defineEmits<AddPromoModalEmits>()

const formData = reactive<PromoFormData>({
  code: '',
  acheteurId: null,
  acheteur: '', // Gardé pour compatibilité
  type: 'percentage',
  montant: '',
  montantMin: '',
  localisation: '1',
  utilisation: 'unique',
  actif: 'true',
  dateDebut: '',
  dateFin: '',
  premiereCommande: false,
  offert: false,
  cumulable: true
})

const buyerAutocompleteRef = ref<{ clearSelection: () => void } | null>(null)

// Messages d'erreur
const validationErrors = ref({
  montant: '',
  montantMin: '',
  dateRange: ''
})

// Validation des montants
const validateMontants = () => {
  const montantMin = parseFloat(formData.montantMin)
  
  // Réinitialiser les erreurs
  validationErrors.value.montantMin = ''

    // Validation montant minimum
    if (formData.montantMin && montantMin < 0) {
      validationErrors.value.montantMin = 'Le montant minimum ne peut pas être négatif'
    }

}

// Validation des dates
const validateDates = () => {
  validationErrors.value.dateRange = ''
  
  if (formData.dateDebut && formData.dateFin) {
    const dateDebut = new Date(formData.dateDebut)
    const dateFin = new Date(formData.dateFin)
    
    if (dateFin <= dateDebut) {
      validationErrors.value.dateRange = 'La date de fin doit être postérieure à la date de début'
    }
  }
}

// Validation du formulaire
const isFormValid = computed(() => {
  // Vérifier que tous les champs requis sont remplis (sauf acheteur et montantMin qui sont optionnels)
  const allFieldsFilled = formData.code && 
                         formData.type && 
                         formData.montant && 
                         formData.localisation && 
                         formData.dateDebut && 
                         formData.dateFin && 
                         formData.utilisation &&
                         formData.actif

  // Vérifier que la date de fin est après la date de début
  const datesValid = formData.dateDebut && formData.dateFin 
    ? new Date(formData.dateFin) > new Date(formData.dateDebut)
    : true

  const montantsValid = !validationErrors.value.montant && 
                       !validationErrors.value.montantMin && 
                       !validationErrors.value.dateRange

  return allFieldsFilled && datesValid && montantsValid
})

// Read-only computed from prop to use in template
const isSubmitting = computed(() => !!props.isSubmitting)

// Watchers pour valider en temps réel
watch([() => formData.montantMin], validateMontants)
watch([() => formData.dateDebut, () => formData.dateFin], validateDates)

const handleSubmit = () => {
  // Convertir l'ID acheteur en string pour compatibilité et appliquer les valeurs par défaut
  const submitData = {
    ...formData,
    acheteur: formData.acheteurId ? formData.acheteurId.toString() : 'tous',
    montantMin: formData.montantMin || '0',
  }
  emit('submit', submitData)
}

const handleBuyerChange = (buyerId: number | null) => {
  formData.acheteurId = buyerId
}

const clearBuyer = () => {
  formData.acheteurId = null
  formData.acheteur = 'tous'
  buyerAutocompleteRef.value?.clearSelection()
}

const handleOffertChange = () => {
  if (formData.offert) {
    // Si "Offert" est coché, mettre le montant à 100% et le type à "pourcentage"
    formData.montant = '100'
    formData.type = 'percentage'
  } else {
    // Si "Offert" est décoché, remettre les valeurs par défaut
    formData.montant = ''
    formData.type = 'percentage'
  }
}

const handleMontantChange = () => {
  const montant = parseFloat(formData.montant)
  
  // Validation selon le type
  if (formData.type === 'percentage') {
    if (montant < 1) {
      validationErrors.value.montant = 'Le pourcentage doit être entre 1% et 100%'
    } else if (montant > 100) {
      validationErrors.value.montant = 'Le pourcentage ne peut pas dépasser 100%'
    } else {
      validationErrors.value.montant = ''
    }
    
    // Si l'utilisateur met 100%, cocher automatiquement "offert"
    if (montant === 100) {
      formData.offert = true
    } else if (montant !== 100) {
      // Si ce n'est plus 100%, décocher "offert"
      formData.offert = false
    }
  } else if (formData.type === 'fixed') {
    if (montant < 0) {
      validationErrors.value.montant = 'Le montant fixe ne peut pas être négatif'
    } else {
      validationErrors.value.montant = ''
    }
  }
}

const handleTypeChange = () => {
  // Si on change de type, décocher "offert" si ce n'est plus un pourcentage
  if (formData.type !== 'percentage') {
    formData.offert = false
  } else {
    // Si on revient en pourcentage, vérifier si c'est 100%
    const montant = parseFloat(formData.montant)
    if (montant === 100) {
      formData.offert = true
    }
  }
}

const handleNumberWheel = (event: WheelEvent) => {
  // Keep page scrolling while preventing wheel from incrementing/decrementing number inputs.
  const input = event.target as HTMLInputElement | null
  input?.blur()
}

const handlePremiereCommandeChange = () => {
  if (formData.premiereCommande) {
    // Si "Réservé aux nouveaux clients" est coché, forcer "Utilisation unique"
    formData.utilisation = 'unique'
  }
  // Si on décoche "Réservé aux nouveaux clients", on laisse l'utilisateur choisir
}

// Reset form when modal opens
watch(() => props.isVisible, (newValue) => {
  if (newValue) {
    Object.assign(formData, {
      code: '',
      acheteurId: null,
      acheteur: 'tous',
      type: 'percentage',
      montant: '',
      montantMin: '',
      localisation: '1',
      // Defaults required: Utilisation = 'unique', Actif = 'true' (Oui)
      utilisation: 'unique',
      actif: 'true',
      dateDebut: '',
      dateFin: '',
      premiereCommande: false,
      offert: false,
      cumulable: true
    })
  }
})
</script> 